import React from 'react';
import { SLOPE_BANDS, DEFAULT_MAX_SLOPE_PERCENT, bandRangeLabel } from './slopeBands';
import { FLOOD_COLORS } from '../atlas/maps/landMapColors';

const FLOOD_LEGEND = [
  { key: 'floodway', label: 'Floodway' },
  { key: 'high_risk', label: '1% annual chance' },
  { key: 'moderate', label: '0.2% annual chance' },
];

/** Compact legend under the map listing only what is currently drawn. */
export default function MapLegendStrip({ hasResult, radiusMiles, placesRadiusMiles, layers, available, contourNote, elevationRange, terrainNote, parcelContourIntervalFt, canopyRadiusFt, footprintsRadiusFt, showNeighbouringFootprints = false, floodRadiusFt }) {
  const items = [];
  if (hasResult) items.push({ key: 'subject', swatch: 'le-legend-dot le-legend-subject', label: 'Subject' });
  if (hasResult) items.push({ key: 'demo', swatch: 'le-legend-line le-legend-dashed-orange', label: `${radiusMiles || 5} mi demographics` });
  if (hasResult && available.places) items.push({ key: 'places', swatch: 'le-legend-line le-legend-teal', label: `${placesRadiusMiles || 3} mi places` });
  if (available.parcel && layers.parcel) items.push({ key: 'parcel', swatch: 'le-legend-line le-legend-parcel', label: 'Parcel' });
  if (available.zone && layers.zone) items.push({ key: 'zone', swatch: 'le-legend-line le-legend-zone', label: 'Zoning district' });
  if (available.terrain && layers.terrain && layers.parcelContours) items.push({ key: 'parcelContours', swatch: 'le-legend-line le-legend-parcel-contour', label: `Parcel contours${parcelContourIntervalFt ? ` · ${parcelContourIntervalFt} ft interval` : ''} · highlight runs high → low` });
  if (available.terrain && layers.terrain && layers.terrainMode === 'slope') {
    const maxSlope = Number(layers.maxSlopePercent) || DEFAULT_MAX_SLOPE_PERCENT;
    items.push({
      key: 'slope',
      custom: (
        <span className="le-legend-bands" aria-hidden="true">
          {SLOPE_BANDS.map((b) => <span key={b.key} className="le-legend-band" style={{ background: b.color }} title={`${b.label} ${bandRangeLabel(b)} · ${b.buildability}`} />)}
        </span>
      ),
      label: 'Slope · flat → hillside (0–2 · 2–5 · 5–8 · 8–12 · 12–20 · 20–30 · 30%+)',
    });
    items.push({ key: 'steep', swatch: 'le-legend-hatch', label: `Steeper than ${maxSlope}% · over your max slope` });
  } else if (available.terrain && layers.terrain) items.push({ key: 'terrain', swatch: 'le-legend-gradient', label: `${elevationRange ? `Elevation ${elevationRange}` : 'Elevation'} · ▼ low ▲ high${terrainNote ? ` · ${terrainNote}` : ''}` });
  if (available.treeCanopy && layers.treeCanopy) items.push({ key: 'canopy', swatch: 'le-legend-canopy', label: `Tree canopy · parcel solid, surroundings faded${Number.isFinite(canopyRadiusFt) ? ` · ${canopyRadiusFt} ft ring` : ''}` });
  if (available.footprints && layers.footprints) {
    const surround = showNeighbouringFootprints && available.footprintsSurround;
    items.push({ key: 'footprints', swatch: 'le-legend-footprint', label: `Building footprints · on-parcel solid, crossing hatched${surround ? ` · neighbours faint${Number.isFinite(footprintsRadiusFt) ? ` · ${footprintsRadiusFt} ft ring` : ''}` : ''}` });
  }
  if (available.flood && layers.flood) {
    const around = available.floodSurround && Number(floodRadiusFt) > 0;
    items.push({
      key: 'flood',
      custom: (
        <span className="le-legend-bands" aria-hidden="true">
          {FLOOD_LEGEND.map((c) => <span key={c.key} className="le-legend-band" style={{ background: FLOOD_COLORS[c.key].fill }} title={c.label} />)}
        </span>
      ),
      label: `Flood zones · floodway · 1% · 0.2% · BFE dashed${around ? ` · ${floodRadiusFt} ft around the lot faded` : ''}`,
    });
  }
  if (available.publicPlaces && layers.publicPlaces) items.push({ key: 'publicPlaces', swatch: 'le-legend-dot le-legend-public', label: 'Public & recreational places' });
  if (available.anchors && layers.anchors) items.push({ key: 'anchors', swatch: 'le-legend-dot le-legend-anchor', label: 'Commercial anchors · size = score' });
  if (available.adverse && layers.externalities) items.push({ key: 'ext', swatch: 'le-legend-dot le-legend-ext', label: 'Adverse uses · size = impact' });
  if (available.comps && layers.comps) items.push({ key: 'comps', swatch: 'le-legend-dot le-legend-comp', label: 'Sale comps · violet new, blue existing · filled active, hollow inactive' });
  if (hasResult && layers.jobRadii) items.push({ key: 'jobs', swatch: 'le-legend-line le-legend-blue', label: '1 / 2 / 5 / 10 mi job radii' });
  if (layers.contours && contourNote) items.push({ key: 'contours', swatch: 'le-legend-line le-legend-contour', label: contourNote });
  if (!items.length) return null;
  return (
    <div className="le-legend-strip" aria-label="Map legend">
      {items.map((it) => (
        <span key={it.key} className="le-legend-strip-item">{it.custom || <span className={it.swatch} />}{it.label}</span>
      ))}
    </div>
  );
}
